import resolveMapboxURL from '@gaia/utils/mapbox/resolveMapboxURL';
import compact from 'lodash/compact';
import fromPairs from 'lodash/fromPairs';
import memoize from 'lodash/memoize';
import startsWith from 'lodash/startsWith';
import type {RequestParameters, ResourceType} from 'mapbox-gl';

// "transformRequest" is a function used by Mapbox GL to modify network requests before they go out.
// See https://docs.mapbox.com/mapbox-gl-js/api/#map for more

// We use "transformRequest" to allow a single Mapbox style to use multiple glyph endpoints.
// Font names are overwritten such that they include both the original style id and the font name.

export const inner = memoize(
    (glyphMap) =>
        (url: string, resourceType: ResourceType): RequestParameters => {
            if (resourceType === 'Glyphs' && startsWith(url, 'g://')) {
                const parts = url.replace('g://', '').split('/');
                const [id, fontstack] = parts[0].split('__');
                const glyphUrl =
                    glyphMap[id] ??
                    'https://static.gaiagps.com/GaiaTopoGL/glyphs/{fontstack}/{range}.pbf';
                return {
                    url: glyphUrl.replace('{fontstack}', fontstack).replace('{range}', parts[1]),
                };
            } else if (
                resourceType === 'Tile' &&
                (startsWith(url, 'https://privatetiles.gaiagps.com') ||
                    startsWith(url, `${window.location.origin}/api/tiles`))
            ) {
                return {url: url, credentials: 'include'};
            } else {
                return {url: url};
            }
        }
);

export default function getTransformRequest(styles: GaiaMapboxStyle[]) {
    const glyphMap = fromPairs(
        compact(
            styles.map((style) => {
                if (style && style.metadata && style.metadata.id && style.glyphs) {
                    return [style.metadata.id, resolveMapboxURL(style.glyphs)];
                } else {
                    return undefined;
                }
            })
        )
    );

    return inner(glyphMap);
}
